import { useEffect, useState } from "react";
import { getEndpointUpdateRollout } from "../../lib/artifactRolloutClient.js";
import { compareSemver } from "../../lib/semver.js";

const placement = {
  computed: "Computed rollout order",
  device_group: "Device group rule",
  endpoint: "Endpoint rule",
  user_group: "User group rule",
};
const audiences = {
  general: "General rollout",
  tenant_main: "Main track",
  tenant_preview: "Preview track",
  tenant_target: "Targeted rollout",
};
const labels = {
  BLOCKED: "Update blocked",
  ELIGIBLE: "Eligible; awaiting report",
  HISTORICAL: "Previous release",
  OBSERVED: "Version reported",
  WAITING: "Waiting for update",
};
const button =
  "rounded-[2px] border border-[color:var(--line)] px-2 py-1 text-[11px] disabled:opacity-40";
function time(value) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime())
    ? `${date.toISOString().slice(0, 19).replace("T", " ")} UTC`
    : "Unavailable";
}

export default function EndpointUpdateRolloutPanel({ endpointId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getEndpointUpdateRollout(endpointId)
      .then((response) => {
        if (active) setData(response);
      })
      .catch((cause) => {
        if (active) {
          setData(null);
          setError(cause.message || "Update history could not be loaded");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [endpointId, refresh]);
  const observedVersions = new Set([
    data?.reportedVersion,
    ...(data?.observations ?? []).flatMap((row) => [row.previousVersion, row.version]),
  ].filter(Boolean));
  const releases = (data?.releases ?? []).filter((row) =>
    observedVersions.has(row.version) || row.firstRetainedObservationAt ||
    row.waves?.some((wave) => wave.openedAt),
  ).sort((left, right) => compareSemver(right.version, left.version));
  const release =
    releases.find((row) => row.publicationId === selected) ?? releases[0];
  const observations =
    data?.observations?.filter(
      (row) =>
        row.version === release?.version ||
        row.previousVersion === release?.version,
    ) ?? [];
  return (
    <section
      aria-busy={loading}
      aria-label="Update rollout"
      className="grid gap-3 rounded-[2px] border border-[color:var(--line)] p-3 text-[12px] text-[color:var(--ink)]"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">Update rollout</h3>
        <button
          type="button"
          className={button}
          disabled={loading}
          onClick={() => setRefresh((value) => value + 1)}
        >
          Refresh updates
        </button>
      </div>
      {loading && !data && <p>Loading update history...</p>}
      {error && (
        <p role="alert" className="text-amber-600">
          {error}
        </p>
      )}
      {data && (
        <>
          <p className="text-[11px] text-[color:var(--muted)]">
            Origin Agent / Checked {time(data.evaluatedAt)}
          </p>
          <p>
            Reported{" "}
            <span className="font-mono">
              {data.reportedVersion || "Unknown"}
            </span>{" "}
            at {time(data.reportedAt)}
          </p>
          {releases.length > 0 && <label className="grid gap-1 text-[color:var(--muted)]">
            Release
            <select
              aria-label="Update release"
              value={release?.publicationId ?? ""}
              onChange={(event) => setSelected(event.target.value)}
              className="min-w-0 rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-2 text-[color:var(--ink)]"
            >
              {releases.map((row) => (
                <option key={row.publicationId} value={row.publicationId}>
                  {row.version} / {audiences[row.audience] ?? row.audience}
                </option>
              ))}
            </select>
          </label>}
          {!release && (
            <p>No recorded rollout history for this endpoint.</p>
          )}
          {release && (
            <>
              <div>
                <h4 className="font-semibold">
                  {labels[
                    release.state?.replace("ENDPOINT_UPDATE_STATE_", "")
                  ] ?? "Status unavailable"}
                </h4>
                <p className="mt-1 text-[color:var(--muted)]">
                  {release.explanation}
                </p>
              </div>
              <dl className="grid gap-3">
                <div>
                  <dt className="text-[color:var(--muted)]">
                    Estimated eligibility
                  </dt>
                  <dd>
                    {release.estimatedEligibleAt
                      ? time(release.estimatedEligibleAt)
                      : release.eligibleNow
                        ? "Eligible now"
                        : "No estimate available"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[color:var(--muted)]">
                    Current endpoint placement
                  </dt>
                  <dd>
                    {release.endpointWave
                      ? `Wave ${release.endpointWave} / `
                      : ""}
                    {placement[release.placementSource] ??
                      release.placementSource}
                  </dd>
                </div>
                <div>
                  <dt className="text-[color:var(--muted)]">
                    First retained report of this version
                  </dt>
                  <dd>
                    {release.firstRetainedObservationAt
                      ? time(release.firstRetainedObservationAt)
                      : "Not found in retained reports"}
                  </dd>
                </div>
              </dl>
              <p className="text-[11px] text-[color:var(--muted)]">
                Estimates assume no pauses or schedule changes. Installation and
                the next report can follow later.
              </p>
              <h4 className="border-t border-[color:var(--line)] pt-3 font-medium">
                Schedule for this release
              </h4>
              <p className="text-[11px] text-[color:var(--muted)]">
                {audiences[release.audience] ?? release.audience} /{" "}
                {release.laneKey} / All times UTC
              </p>
              <ol className="grid gap-3 border-l border-[color:var(--line)] pl-3">
                {(release.waves ?? []).map((wave) => (
                  <li key={wave.position}>
                    <div className="font-medium">
                      Wave {wave.position} / {wave.cumulativePercent}%
                      {wave.position === release.endpointWave
                        ? " / This endpoint"
                        : ""}
                    </div>
                    <p className="text-[11px] text-[color:var(--muted)]">
                      {wave.openedAt
                        ? `Opened ${time(wave.openedAt)}`
                        : wave.estimatedOpenAt
                          ? `Estimated ${time(wave.estimatedOpenAt)}`
                          : "Timing unavailable"}
                    </p>
                  </li>
                ))}
              </ol>
              {!release.waves?.length && (
                <p>No wave schedule was recorded for this publication.</p>
              )}
              <p className="text-[11px] text-[color:var(--muted)]">
                {release.historyNote}
              </p>
              <h4 className="border-t border-[color:var(--line)] pt-3 font-medium">
                Reported version history
              </h4>
              <ul className="grid gap-2">
                {observations.map((row, index) => (
                  <li key={index}>
                    <span className="font-mono">
                      {row.previousVersion || "Unknown"} to {row.version}
                    </span>
                    <div className="text-[11px] text-[color:var(--muted)]">
                      Observed {time(row.observedAt)}
                    </div>
                  </li>
                ))}
              </ul>
              {!observations.length && (
                <p>No retained version transitions for this release.</p>
              )}
            </>
          )}
          {(data.releasesTruncated || data.observationsTruncated) && (
            <p className="text-amber-600">
              History is limited to the latest 20 publications and 200 version
              transitions.
            </p>
          )}
        </>
      )}
    </section>
  );
}
